import React, { useCallback, useEffect, useState } from "react";
import Header from "@/components/layout/Header";
import LiveBanner from "@/components/layout/LiveBanner";
import DhanCard from "@/components/dashboard/DhanCard";
import DeltaCard from "@/components/dashboard/DeltaCard";
import IbkrCard from "@/components/dashboard/IbkrCard";
import WebhookCard from "@/components/dashboard/WebhookCard";
import EmaPanel from "@/components/dashboard/EmaPanel";
import AtrPanel from "@/components/dashboard/AtrPanel";
import AlertsConfig from "@/components/dashboard/AlertsConfig";
import BacktestPanel from "@/components/dashboard/BacktestPanel";
import PositionsTable from "@/components/dashboard/PositionsTable";
import TradeLog from "@/components/dashboard/TradeLog";
import WebhookLog from "@/components/dashboard/WebhookLog";
import ComplianceCard from "@/components/dashboard/ComplianceCard";
import ManualOrderCard from "@/components/dashboard/ManualOrderCard";
import PortfolioRiskCard from "@/components/dashboard/PortfolioRiskCard";
import ScreenerPanel from "@/components/dashboard/ScreenerPanel";
import ScreenerResults from "@/components/dashboard/ScreenerResults";
import { api } from "@/lib/api";

export default function Dashboard({ user }) {
  const [status, setStatus] = useState(null);
  const [screenerRefresh, setScreenerRefresh] = useState(0);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get("/brokers/status");
      setStatus(res.data);
    } catch (e) {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 30000);
    return () => clearInterval(t);
  }, [loadStatus]);

  const anyAuth =
    !!status?.dhan?.is_authenticated ||
    !!status?.delta_exchange?.is_authenticated ||
    !!status?.interactive_brokers?.is_authenticated;

  return (
    <div
      className="min-h-screen bg-surface-1 text-foreground"
      data-testid="dashboard-page"
    >
      <LiveBanner />
      <Header user={user} />

      <main className="max-w-[1600px] mx-auto px-6 py-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 pb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              / control room
            </div>
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight mt-1">
              Welcome, {user?.name?.split(" ")[0] || "Trader"}
            </h1>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground tracking-wider">
            {new Date().toLocaleString("en-IN", { hour12: false })}
          </div>
        </div>

        {/* Brokers grid */}
        <section data-testid="brokers-section">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3">
            / brokers
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <DhanCard status={status?.dhan} reload={loadStatus} />
            <DeltaCard status={status?.delta_exchange} reload={loadStatus} />
            <IbkrCard status={status?.interactive_brokers} reload={loadStatus} />
          </div>
        </section>

        {/* Ops row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <WebhookCard webhookToken={status?.webhook_token} webhookUrl={status?.webhook_url} />
          <EmaPanel anyAuthenticated={anyAuth} />
          <AtrPanel anyAuthenticated={anyAuth} />
          <ComplianceCard />
        </div>

        {/* Positions + Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <PositionsTable anyAuthenticated={anyAuth} />
          </div>
          <AlertsConfig />
        </div>

        {/* Portfolio Risk (full-width) */}
        <PortfolioRiskCard anyAuthenticated={anyAuth} />

        {/* Manual Order */}
        <ManualOrderCard brokersStatus={status} reload={loadStatus} />

        {/* Screener */}
        <ScreenerPanel onResultsUpdate={() => setScreenerRefresh(n => n + 1)} />

        {/* Backtest (full-width) */}
        <BacktestPanel />

        {/* Logs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <WebhookLog />
          <TradeLog />
        </div>

        {/* Screener Results (bottom) */}
        <ScreenerResults refreshKey={screenerRefresh} />

        <footer className="pt-4 pb-8 text-[10px] font-mono text-muted-foreground tracking-wider flex items-center justify-between">
          <span>
            chartink-trade · v1.1 ·{" "}
            <span className="text-brand">multi-broker</span>
          </span>
          <span>NSE / BSE · live-trading</span>
        </footer>
      </main>
    </div>
  );
}
